import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { leafCanonicalLink, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { CITY_HUBS } from "@/data/cityHubs";
import beforeImg from "@/assets/recover-awning-green-canvas-before-san-antonio.jpg";
import hero from "@/assets/awning-blue-striped-fabric.jpg";

import heroPicture from "@/assets/awning-blue-striped-fabric.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/recover-awnings")({
  head: (ctx) => ({
    // Self-canonical: /recover-awnings is its own leaf page.
    links: leafCanonicalLink(ctx, "/recover-awnings"),
    meta: routeMeta(ctx, {
      title: "Awning Recover San Antonio — Recover Existing Frame & Canopy | Texas Made Shade",
      description: "San Antonio awning recover company. We recover awnings and canopies on your existing frame — Sunbrella & Tempotest canvas, patterned.",
      image: beforeImg,
      imageAlt: "Faded green canvas storefront awning in San Antonio needing a recover on the existing aluminum frame",
      socialTitle: "Recover Awning & Canopy — San Antonio Awning Recover Company",
      socialDescription: "Recover your existing awning frame with new Sunbrella or Tempotest canvas. Built in Seguin, TX.",
      keywords: "recover awning, awning recover, recover existing frame, canopy recover, san antonio awning recover company, awning re-cover, recover canopy san antonio, replace awning fabric, awning fabric replacement Texas, sunbrella recover",
    }),
    scripts: serviceHeadScripts({
      path: "/recover-awnings",
      serviceName: "Awning & Canopy Recover",
      breadcrumbName: "Awning Recover",
      description: "Awning and canopy recover service — new Sunbrella and Tempotest canvas patterned, sewn and re-fitted to your existing frame.",
    }),
  }),
  component: ServiceRoute,
});

function ServiceRoute() {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  if (leaf?.routeId !== Route.id) return <Outlet />;
  return (
    <ServicePage
      currentPath="/recover-awnings"
      serviceName="Awning & Canopy Recover"
      eyebrow="★ AWNING & CANOPY RECOVER ★"
      title="Recover Your Existing Awning Frame — San Antonio Awning Recover Company"
      intro="Already have an awning or canopy frame in good shape? We recover it. New Sunbrella® or Tempotest® canvas — hand-patterned to your existing frame, sewn in our Seguin shop, and re-installed by the same crew. A fraction of the cost of a full replacement."
      heroImage={heroPicture}
      features={[
        "Recover your existing awning or canopy frame",
        "Sunbrella® and Tempotest® premium acrylic canvas",
        "Hand-patterned on-site for a perfect fit",
        "Hundreds of solid colors, stripes and patterns",
        "Save 50%+ vs. a full new awning",
        "Serving San Antonio, Austin, Seguin and all of South Texas",
      ]}
      body={[
        { heading: "Why Recover Instead of Replace?", text: "If your aluminum or steel awning frame is structurally sound, there's no reason to scrap it. The fabric is what fails first under Texas sun — frames last 20+ years. A canvas recover gives you a brand-new awning at a fraction of the cost." },
        { heading: "How an Awning Recover Works", text: "We come out, inspect the existing frame, take exact field patterns, and pull the old canvas. Back in the Seguin shop we cut and sew the new Sunbrella or Tempotest canvas, then return to stretch and fasten it to your frame. Most recovers turn around in 1–2 weeks." },
        { heading: "San Antonio Awning Recover Company", text: "We are the go-to awning recover company for San Antonio storefronts, restaurants, patios and porches. We also recover commercial canopies, walkway covers and entry awnings throughout Bexar, Comal, Guadalupe and Travis counties." },
        { heading: "Frame Repairs (If Needed)", text: "If your frame has a bent rafter, cracked weld or rusted spot, our welders can repair it in the same visit — TIG-welded aluminum or steel — so the new canvas goes onto a frame that's ready for another decade." },
      ]}
      applications={["Storefront Awnings", "Restaurant Canopies", "Patio Awnings", "Window Awnings", "Walkway Covers", "Entry Domes", "RV & Porch Awnings"]}
      faqs={[
        { q: "How much does it cost to recover an awning?", a: "An awning recover typically runs 40–60% of the cost of a brand-new awning of the same size, because you're keeping the existing welded frame. We give a firm written quote after a free on-site measure." },
        { q: "Do you recover awnings in San Antonio?", a: "Yes — we are a San Antonio awning recover company. We recover storefront, restaurant, patio and commercial canopies throughout the San Antonio metro from our shop in nearby Seguin, TX." },
        { q: "What fabric do you use for recovers?", a: "Sunbrella® and Tempotest® solution-dyed acrylic canvas. Both carry a 10-year color-fast warranty and are the only fabrics we trust for Texas sun." },
        { q: "Can you match my old awning's color or stripe?", a: "In almost every case, yes. Sunbrella alone offers hundreds of solid colors and awning stripes. We bring fabric samples to your site so you can match — or upgrade — the original look." },
        { q: "How long does an awning recover take?", a: "Most residential and small storefront recovers turn around in 1–2 weeks from the day we pattern. Larger commercial canopies may take 2–4 weeks depending on fabric lead time." },
      ]}
      preContent={(
        <section
          aria-labelledby="awning-recover-before-after"
          className="mx-auto max-w-6xl px-4 py-16 md:py-20"
        >
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ Awning Recover Project · San Antonio, TX
          </div>
          <h2
            id="awning-recover-before-after"
            className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl"
          >
            Storefront Awning Recover — Before & After
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
            A classic downtown brick storefront with a sun-tired green canvas awning. The welded
            aluminum frame is still solid — only the fabric needs replacing. We're recovering this
            existing frame with new Sunbrella® canvas. <strong>After photo coming soon.</strong>
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-2 md:gap-8">
            <figure className="overflow-hidden border-2 border-navy-deep/15 bg-white shadow-card">
              <div className="relative">
                <img
                  src={beforeImg}
                  alt="Faded and sagging green canvas storefront awning on a brick building in San Antonio, TX — existing welded aluminum frame is sound and ready for a recover by Texas Made Shade"
                  width={1200}
                  height={1600}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[3/4] w-full object-cover"
                />
                <span className="absolute left-3 top-3 bg-navy-deep px-3 py-1 font-display text-xs uppercase tracking-[0.3em] text-white">
                  Before
                </span>
              </div>
              <figcaption className="border-t-2 border-navy-deep bg-cream px-5 py-4">
                <div className="font-display text-base font-bold uppercase tracking-wider text-navy-deep">
                  Before · Sun-Tired Green Canvas
                </div>
                <p className="mt-1 text-sm text-navy-deep/75">
                  Years of San Antonio sun left the canvas faded and sagging. The welded aluminum
                  frame underneath is still straight and solid — a perfect candidate for an awning
                  recover instead of a full replacement.
                </p>
              </figcaption>
            </figure>

            <figure className="overflow-hidden border-2 border-dashed border-red/40 bg-cream/60 shadow-card">
              <div className="relative flex aspect-[3/4] w-full items-center justify-center bg-cream">
                <div className="px-6 text-center">
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                    After Photo Coming Soon
                  </div>
                  <p className="mt-3 font-display text-2xl font-bold text-navy-deep">
                    New Sunbrella® Recover
                  </p>
                  <p className="mt-2 text-sm text-navy-deep/70">
                    Same frame. Brand-new canvas. Photo will be posted as soon as the install is
                    complete.
                  </p>
                </div>
                <span className="absolute left-3 top-3 bg-red px-3 py-1 font-display text-xs uppercase tracking-[0.3em] text-white">
                  After
                </span>
              </div>
              <figcaption className="border-t-2 border-red bg-cream px-5 py-4">
                <div className="font-display text-base font-bold uppercase tracking-wider text-navy-deep">
                  After · New Canvas on the Existing Frame
                </div>
                <p className="mt-1 text-sm text-navy-deep/75">
                  Recovered with new Sunbrella® acrylic canvas — color-fast, mildew-resistant, and
                  good for another decade of Texas sun on the same frame.
                </p>
              </figcaption>
            </figure>
          </div>
        </section>
      )}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">★ AWNING RECOVER BY CITY</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Awning Recovers Across Central & South Texas
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-base text-navy-deep/75">
                Pick your city for a dedicated awning &amp; canopy recover page — same Sunbrella® and Tempotest® canvas, sewn in our Seguin, TX shop, installed locally.
              </p>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {CITY_HUBS.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/recover-awnings/in/$city"
                    params={{ city: c.slug }}
                    className="block border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Awning Recovers in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    />
  );
}

